import React, { useEffect, useMemo, useState } from 'react';
import { FaTimes, FaSpinner, FaTag } from 'react-icons/fa';
import {
  CREATIVE_SAMURAI_COURSE_FEE,
  formatCourseFee,
} from '../../../config/constants/creativeSamurai';
import {
  getAmountPaid,
  getDiscountAmount,
  getRemainingBalance,
} from '../../../utils/paymentStatus';

const DISCOUNT_PRESETS = [
  { label: 'No discount', value: 0 },
  { label: '10% off', value: Math.round(CREATIVE_SAMURAI_COURSE_FEE * 0.1) },
  { label: '20% off', value: Math.round(CREATIVE_SAMURAI_COURSE_FEE * 0.2) },
];

const PaymentModal = ({
  isOpen,
  enrollment,
  isSubmitting,
  onClose,
  onSubmit,
}) => {
  const [amountPaid, setAmountPaid] = useState('');
  const [discountAmount, setDiscountAmount] = useState('');
  const [discountMode, setDiscountMode] = useState('preset');

  useEffect(() => {
    if (isOpen && enrollment) {
      setAmountPaid(String(getAmountPaid(enrollment)));
      setDiscountAmount(String(getDiscountAmount(enrollment)));
      const existingDiscount = getDiscountAmount(enrollment);
      const isPreset = DISCOUNT_PRESETS.some((preset) => preset.value === existingDiscount);
      setDiscountMode(isPreset ? 'preset' : 'custom');
    }
  }, [isOpen, enrollment]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const parsedAmount = Number(amountPaid);
  const parsedDiscount = Number(discountAmount) || 0;
  const currentPaid = enrollment ? getAmountPaid(enrollment) : 0;
  const effectiveFee = Math.max(0, CREATIVE_SAMURAI_COURSE_FEE - parsedDiscount);

  const isValidDiscount =
    Number.isFinite(parsedDiscount) &&
    parsedDiscount >= 0 &&
    parsedDiscount <= CREATIVE_SAMURAI_COURSE_FEE;

  const isValidAmount =
    Number.isFinite(parsedAmount) &&
    parsedAmount >= currentPaid &&
    parsedAmount <= effectiveFee;

  const remainingAfter = Number.isFinite(parsedAmount)
    ? Math.max(0, effectiveFee - parsedAmount)
    : enrollment
      ? getRemainingBalance(enrollment)
      : 0;

  const paymentProgress = effectiveFee > 0
    ? Math.min(100, (parsedAmount / effectiveFee) * 100)
    : 100;

  const savingsPercent = useMemo(() => {
    if (!parsedDiscount) return 0;
    return Math.round((parsedDiscount / CREATIVE_SAMURAI_COURSE_FEE) * 100);
  }, [parsedDiscount]);

  if (!isOpen || !enrollment) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isValidAmount || !isValidDiscount) return;
    onSubmit(enrollment, { amountPaid: parsedAmount, discountAmount: parsedDiscount });
  };

  const handleMarkFullPaid = () => {
    setAmountPaid(String(effectiveFee));
  };

  const handlePresetSelect = (value) => {
    setDiscountAmount(String(value));
    setDiscountMode('preset');
    const newEffectiveFee = CREATIVE_SAMURAI_COURSE_FEE - value;
    if (Number(amountPaid) > newEffectiveFee) {
      setAmountPaid(String(Math.max(currentPaid, newEffectiveFee)));
    }
  };

  const handleCustomDiscount = (value) => {
    setDiscountAmount(value);
    setDiscountMode('custom');
    const discount = Number(value) || 0;
    const newEffectiveFee = CREATIVE_SAMURAI_COURSE_FEE - discount;
    if (Number(amountPaid) > newEffectiveFee) {
      setAmountPaid(String(Math.max(currentPaid, newEffectiveFee)));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        aria-label="Close payment dialog"
      />

      <div
        className="relative w-full max-w-lg bg-white rounded-xl shadow-2xl border border-gray-200/80 overflow-hidden max-h-[90vh] overflow-y-auto"
        role="dialog"
        aria-modal="true"
        aria-labelledby="payment-modal-title"
      >
        <div className="px-6 py-5 border-b border-gray-100 bg-gray-50/80">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 id="payment-modal-title" className="text-lg font-bold text-designer-dark">
                Record Payment
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {enrollment.name}
                {enrollment.student_id && (
                  <span className="ml-2 font-mono text-xs bg-gray-100 px-2 py-0.5 rounded">
                    {enrollment.student_id}
                  </span>
                )}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              aria-label="Close"
            >
              <FaTimes />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Fee breakdown receipt */}
          <div className="rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
              <FaTag className="w-3.5 h-3.5 text-designer-primary" />
              <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Fee Breakdown</span>
            </div>
            <div className="p-4 space-y-2.5">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">List price</span>
                <span className="font-medium text-gray-700 tabular-nums">{formatCourseFee(CREATIVE_SAMURAI_COURSE_FEE)}</span>
              </div>
              {parsedDiscount > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-green-600 flex items-center gap-1.5">
                    Discount
                    <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-semibold">
                      -{savingsPercent}%
                    </span>
                  </span>
                  <span className="font-medium text-green-600 tabular-nums">-{formatCourseFee(parsedDiscount)}</span>
                </div>
              )}
              <div className="flex items-center justify-between pt-2 border-t border-dashed border-gray-200">
                <span className="text-sm font-semibold text-designer-dark">Effective fee</span>
                <span className="text-lg font-bold text-designer-primary tabular-nums">{formatCourseFee(effectiveFee)}</span>
              </div>
            </div>
          </div>

          {/* Discount selection */}
          <div>
            <label className="block text-sm font-medium text-designer-dark mb-2">
              Apply Discount
            </label>
            <div className="flex flex-wrap gap-2 mb-3">
              {DISCOUNT_PRESETS.map((preset) => {
                const isActive = discountMode === 'preset' && parsedDiscount === preset.value;
                return (
                  <button
                    key={preset.label}
                    type="button"
                    onClick={() => handlePresetSelect(preset.value)}
                    disabled={isSubmitting}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all disabled:opacity-50 ${
                      isActive
                        ? 'bg-green-600 text-white border-green-600 shadow-sm'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-green-300 hover:bg-green-50'
                    }`}
                  >
                    {preset.label}
                  </button>
                );
              })}
              <button
                type="button"
                onClick={() => setDiscountMode('custom')}
                disabled={isSubmitting}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all disabled:opacity-50 ${
                  discountMode === 'custom'
                    ? 'bg-green-600 text-white border-green-600 shadow-sm'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-green-300 hover:bg-green-50'
                }`}
              >
                Custom
              </button>
            </div>
            {discountMode === 'custom' && (
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-medium">৳</span>
                <input
                  type="number"
                  min="0"
                  max={CREATIVE_SAMURAI_COURSE_FEE}
                  step="1"
                  value={discountAmount}
                  onChange={(e) => handleCustomDiscount(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-300 rounded-xl text-designer-dark text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  disabled={isSubmitting}
                  placeholder="Enter discount amount"
                />
              </div>
            )}
          </div>

          {/* Already paid indicator */}
          {currentPaid > 0 && (
            <div className="rounded-xl bg-amber-50 p-3 border border-amber-100 flex items-center justify-between">
              <p className="text-xs text-amber-700 font-medium">Already paid</p>
              <p className="text-sm font-bold text-amber-700 tabular-nums">{formatCourseFee(currentPaid)}</p>
            </div>
          )}

          {/* Payment amount */}
          <div>
            <label htmlFor="amount-paid" className="block text-sm font-medium text-designer-dark mb-2">
              Total Amount Paid
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-medium">৳</span>
              <input
                id="amount-paid"
                type="number"
                min={currentPaid}
                max={effectiveFee}
                step="1"
                value={amountPaid}
                onChange={(e) => setAmountPaid(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-designer-dark focus:outline-none focus:ring-2 focus:ring-designer-primary focus:border-transparent"
                disabled={isSubmitting}
                required
              />
            </div>

            {/* Progress bar */}
            <div className="mt-3">
              <div className="flex items-center justify-between text-xs text-gray-500 mb-1.5">
                <span>Payment progress</span>
                <span className="font-semibold tabular-nums">{Math.round(paymentProgress)}%</span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-designer-primary to-green-500 rounded-full transition-all duration-300"
                  style={{ width: `${paymentProgress}%` }}
                />
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Remaining after save:{' '}
                <span className="font-semibold text-designer-dark tabular-nums">
                  {formatCourseFee(remainingAfter)}
                </span>
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleMarkFullPaid}
              disabled={isSubmitting}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 transition-colors disabled:opacity-50"
            >
              Set Full Amount ({formatCourseFee(effectiveFee)})
            </button>
          </div>

          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 px-4 py-3 rounded-xl border border-gray-200 text-gray-600 font-semibold hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !isValidAmount || !isValidDiscount}
              className="flex-1 px-4 py-3 rounded-xl bg-designer-primary hover:bg-red-600 text-white font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <FaSpinner className="animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Payment'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PaymentModal;
